import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBold,
  faItalic,
  faListOl,
  faListUl,
  faLink as faLinkIcon,
  faUnderline as faUnderlineIcon
} from '@fortawesome/free-solid-svg-icons'

import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Collaboration from '@tiptap/extension-collaboration'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import CollaborationCursor from '@tiptap/extension-collaboration-cursor'

import { ReactComponent as IconPlusCircle } from 'assets/icons/plus-circle.svg'
import { ReactComponent as IconTrash } from 'assets/icons/trash-icon.svg'

import TouchableOpacity from 'components/TouchableOpacity'

import './styles.scss'

const normalizeStepsDraft = session => {
  const raw = session?.actionSteps || session?.steps || []
  if (!Array.isArray(raw)) return []

  let maxId = 0
  const steps = raw.map((x, idx) => {
    const idNum = Number(x?.id)
    const id = Number.isFinite(idNum) ? idNum : idx + 1
    if (id > maxId) maxId = id
    return {
      id,
      description:
        typeof x?.description === 'string' ? x.description : '',
      status: !!x?.status
    }
  })

  // Ensure IDs are unique and monotonically assigned.
  const seen = new Set()
  return steps.map(s => {
    if (!seen.has(s.id)) {
      seen.add(s.id)
      return s
    }
    maxId += 1
    return { ...s, id: maxId }
  })
}

const toPersistedSteps = stepsDraft => {
  if (!Array.isArray(stepsDraft)) return []
  return stepsDraft
    .map(s => ({
      id: s?.id,
      description:
        typeof s?.description === 'string' ? s.description : '',
      status: !!s?.status
    }))
    .filter(s => (s.description || '').trim() || s.status)
}

const DEFAULT_SESSION_NOTES_HTML = `
<p><strong>Agenda</strong></p>
<ul><li><p></p></li></ul>
<p><strong>Notes</strong></p>
<ul><li><p></p></li></ul>
`

const toDateInputValue = value => {
  if (!value) return ''
  if (typeof value === 'string') {
    // Already yyyy-mm-dd
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
    const d = new Date(value)
    if (!Number.isNaN(d.getTime()))
      return d.toISOString().slice(0, 10)
    return ''
  }
  const d = new Date(value)
  if (!Number.isNaN(d.getTime()))
    return d.toISOString().slice(0, 10)
  return ''
}

const toDisplayDate = value => {
  if (!value) return ''
  if (typeof value === 'string') {
    // If it's already a friendly label like "Jan 23, 2026"
    if (/[A-Za-z]{3,}\s+\d{1,2},\s+\d{4}/.test(value))
      return value
    const d = new Date(value)
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }
    return value
  }
  const d = new Date(value)
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }
  return ''
}

const normalizeLinkHref = rawUrl => {
  const url = (rawUrl || '').trim()
  if (!url) return ''

  // Allow internal anchors/relative links exactly as entered.
  if (url.startsWith('#') || url.startsWith('/')) return url

  // Keep common schemes as-is.
  if (
    url.startsWith('http://') ||
    url.startsWith('https://') ||
    url.startsWith('mailto:') ||
    url.startsWith('tel:') ||
    url.startsWith('//')
  ) {
    return url
  }

  // If user provided any other scheme (e.g. "ftp:"), keep it.
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(url)) return url

  // Otherwise treat it as an external URL and default to https
  return `https://${url}`
}

const CoachingSessionCollabEditor = ({
  ydoc,
  awareness,
  userIdentity,
  coachingSession,
  hasRemoteState,
  isSaving,
  embedded,
  onSaveMeta,
  onDeleteSession,
  onSaveSteps
}) => {
  const canEditMeta = typeof onSaveMeta === 'function'
  const canDeleteSession = typeof onDeleteSession === 'function'
  const canEditSteps = typeof onSaveSteps === 'function'
  const isEmbedded = !!embedded

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          history: false
        }),
        Underline,
        Link.configure({
          openOnClick: true,
          autolink: true,
          linkOnPaste: true
        }),
        Collaboration.configure({
          document: ydoc,
          field: 'notes'
        }),
        ...(awareness
          ? [
              CollaborationCursor.configure({
                provider: { awareness },
                user: userIdentity || undefined
              })
            ]
          : [])
      ],
      editorProps: {
        attributes: {
          class: 'tiptap',
          style:
            'min-height: 220px; padding: 0; border: none; border-radius: 0; background: transparent; line-height: 1; font-family: inherit;'
        }
      }
    },
    [awareness, userIdentity, ydoc]
  )

  const [bootstrapped, setBootstrapped] = useState(false)
  const [isEditingMeta, setIsEditingMeta] = useState(false)
  const [metaTitle, setMetaTitle] = useState('')
  const [metaDate, setMetaDate] = useState('')
  const [metaError, setMetaError] = useState('')

  const [stepsDraft, setStepsDraft] = useState([])
  const stepsSaveTimerRef = useRef(null)
  const isEditingStepsRef = useRef(false)
  const stepsBlurTimerRef = useRef(null)

  const [isLinkPopoverOpen, setIsLinkPopoverOpen] = useState(
    false
  )
  const [linkUrlDraft, setLinkUrlDraft] = useState('')
  const linkUrlInputRef = useRef(null)

  const stepsSignature = useMemo(() => {
    if (!coachingSession?.id) return ''
    const normalized = normalizeStepsDraft(coachingSession)
    return JSON.stringify(
      normalized.map(s => ({
        id: s.id,
        description: s.description,
        status: !!s.status
      }))
    )
  }, [
    coachingSession?.id,
    coachingSession?.actionSteps,
    coachingSession?.steps
  ])

  useEffect(() => {
    return () => {
      if (stepsSaveTimerRef.current) {
        clearTimeout(stepsSaveTimerRef.current)
      }
      if (stepsBlurTimerRef.current) {
        clearTimeout(stepsBlurTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!isLinkPopoverOpen) return
    // Focus after render so it's easy to paste a URL.
    setTimeout(() => {
      try {
        if (linkUrlInputRef.current?.focus)
          linkUrlInputRef.current.focus()
      } catch (e) {
        // ignore
      }
    }, 0)
  }, [isLinkPopoverOpen])

  useEffect(() => {
    // Only reset the edit form when switching between sessions.
    // (Some queries refresh `coachingSession` object identity, which would otherwise
    // force-exit edit mode while the user is typing.)
    if (!coachingSession?.id) return
    if (isEditingMeta) return
    setMetaTitle(coachingSession?.title || '')
    setMetaDate(
      toDateInputValue(coachingSession?.date) ||
        toDateInputValue(coachingSession?.createdAt) ||
        ''
    )
    setMetaError('')
    setIsEditingMeta(false)
  }, [coachingSession?.id])

  useEffect(() => {
    if (!coachingSession?.id) return
    if (isEditingStepsRef.current) return
    setStepsDraft(normalizeStepsDraft(coachingSession))
  }, [coachingSession?.id, stepsSignature])

  const markStepsEditing = () => {
    isEditingStepsRef.current = true
    if (stepsBlurTimerRef.current) {
      clearTimeout(stepsBlurTimerRef.current)
      stepsBlurTimerRef.current = null
    }
  }

  const markStepsNotEditingSoon = () => {
    if (stepsBlurTimerRef.current) {
      clearTimeout(stepsBlurTimerRef.current)
    }
    stepsBlurTimerRef.current = setTimeout(() => {
      isEditingStepsRef.current = false
    }, 300)
  }

  useEffect(() => {
    if (!editor) return
    if (hasRemoteState) return
    if (bootstrapped) return
    if (!coachingSession) return
    const notesFragment = ydoc?.getXmlFragment
      ? ydoc.getXmlFragment('notes')
      : null
    const hasExistingDocNotes =
      Number(notesFragment?.length || 0) > 0
    if (hasExistingDocNotes) {
      setBootstrapped(true)
      return
    }

    const legacyNotes =
      typeof coachingSession?.notes === 'string'
        ? coachingSession.notes
        : ''

    const initialNotes =
      legacyNotes || DEFAULT_SESSION_NOTES_HTML
    editor.commands.setContent(initialNotes, false)

    setBootstrapped(true)
  }, [
    bootstrapped,
    coachingSession,
    editor,
    hasRemoteState,
    ydoc
  ])

  const openLinkPopover = () => {
    if (!editor) return
    const prev = editor.getAttributes('link').href
    setLinkUrlDraft(typeof prev === 'string' ? prev : '')
    setIsLinkPopoverOpen(true)
  }

  const closeLinkPopover = () => {
    setIsLinkPopoverOpen(false)
    setLinkUrlDraft('')
  }

  const applyLink = () => {
    if (!editor) return
    const url = normalizeLinkHref(linkUrlDraft)
    if (!url) {
      editor.chain().focus().unsetLink().run()
      closeLinkPopover()
      return
    }
    editor.chain().focus().setLink({ href: url }).run()
    closeLinkPopover()
  }

  const blurNotesEditor = () => {
    try {
      if (editor?.commands?.blur) editor.commands.blur()
    } catch (e) {
      // ignore
    }
    try {
      if (editor?.view?.dom?.blur) editor.view.dom.blur()
    } catch (e) {
      // ignore
    }
  }

  const handleSaveMeta = () => {
    const title = (metaTitle || '').trim()
    if (!title) {
      setMetaError('Title is required.')
      return
    }
    setMetaError('')
    if (onSaveMeta) {
      onSaveMeta({
        title,
        date: metaDate || ''
      })
    }
    if (!isEmbedded) setIsEditingMeta(false)
  }

  const scheduleSaveSteps = nextDraft => {
    if (!canEditSteps) return

    if (stepsSaveTimerRef.current) {
      clearTimeout(stepsSaveTimerRef.current)
    }

    stepsSaveTimerRef.current = setTimeout(() => {
      if (!canEditSteps) return
      onSaveSteps(toPersistedSteps(nextDraft))
    }, 700)
  }

  const addStep = () => {
    setStepsDraft(prev => {
      const maxId = prev.reduce((m, s) => {
        const id = Number(s?.id)
        if (!Number.isFinite(id)) return m
        return Math.max(m, id)
      }, 0)
      const next = [
        ...prev,
        { id: maxId + 1, description: '', status: false }
      ]
      scheduleSaveSteps(next)
      return next
    })
  }

  const removeStep = id => {
    setStepsDraft(prev => {
      const next = prev.filter(s => Number(s?.id) !== Number(id))
      scheduleSaveSteps(next)
      return next
    })
  }

  const updateStep = (id, patch) => {
    setStepsDraft(prev => {
      const next = prev.map(s =>
        Number(s?.id) === Number(id) ? { ...s, ...patch } : s
      )
      scheduleSaveSteps(next)
      return next
    })
  }

  return (
    <div style={{ padding: isEmbedded ? 0 : 24 }}>
      {!isEmbedded ? (
        <div
          className="d-flex align-items-start justify-content-between mb-15"
          style={{ gap: 12 }}
        >
          <div style={{ flex: 1 }}>
            {isEditingMeta && canEditMeta ? (
              <div
                onMouseDownCapture={e => e.stopPropagation()}
                onKeyDownCapture={e => e.stopPropagation()}
                onBeforeInputCapture={e => e.stopPropagation()}
              >
                <input
                  className="form-control mb-10"
                  placeholder="Session title"
                  value={metaTitle}
                  autoFocus
                  onFocus={blurNotesEditor}
                  onMouseDown={e => e.stopPropagation()}
                  onKeyDown={e => e.stopPropagation()}
                  onChange={e => setMetaTitle(e.target.value)}
                />
                <input
                  className="form-control"
                  type="date"
                  value={metaDate}
                  onFocus={blurNotesEditor}
                  onMouseDown={e => e.stopPropagation()}
                  onKeyDown={e => e.stopPropagation()}
                  onChange={e => setMetaDate(e.target.value)}
                />
                {metaError ? (
                  <div className="text-danger mt-10">
                    {metaError}
                  </div>
                ) : null}
              </div>
            ) : (
              <>
                <div
                  className="font-weight-bold"
                  style={{ fontSize: 18 }}
                >
                  {(coachingSession?.title || '').trim() ||
                    'Untitled Session'}
                </div>
                <div
                  className="text-muted"
                  style={{ fontSize: 12 }}
                >
                  {toDisplayDate(coachingSession?.date)}
                </div>
              </>
            )}
          </div>

          <div
            className="d-flex align-items-center"
            style={{
              gap: 8,
              flexWrap: 'wrap',
              justifyContent: 'flex-end'
            }}
          >
            {isSaving ? (
              <div
                className="text-muted"
                style={{ fontSize: 12 }}
              >
                Saving…
              </div>
            ) : null}

            {isEditingMeta && canEditMeta ? (
              <>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleSaveMeta}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    setIsEditingMeta(false)
                    setMetaTitle(coachingSession?.title || '')
                    setMetaDate(
                      toDateInputValue(coachingSession?.date) ||
                        ''
                    )
                    setMetaError('')
                  }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                {canEditMeta ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setIsEditingMeta(true)}
                  >
                    Edit
                  </button>
                ) : null}
                {canDeleteSession ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => {
                      const ok = window.confirm(
                        'Delete this coaching session? This cannot be undone.'
                      )
                      if (!ok) return
                      onDeleteSession()
                    }}
                  >
                    Delete
                  </button>
                ) : null}
              </>
            )}
          </div>
        </div>
      ) : null}

      {isEmbedded && canEditMeta ? (
        <div className="mb-15">
          <div
            className="d-flex align-items-end"
            style={{ gap: 10, flexWrap: 'wrap' }}
            onMouseDownCapture={e => e.stopPropagation()}
            onKeyDownCapture={e => e.stopPropagation()}
            onBeforeInputCapture={e => e.stopPropagation()}
          >
            <div style={{ flex: 1, minWidth: 220 }}>
              <div
                className="text-muted"
                style={{ fontSize: 12 }}
              >
                Title
              </div>
              <input
                className="form-control"
                placeholder="Session title"
                value={metaTitle}
                onFocus={blurNotesEditor}
                onChange={e => setMetaTitle(e.target.value)}
              />
            </div>
            <div style={{ minWidth: 180 }}>
              <div
                className="text-muted"
                style={{ fontSize: 12 }}
              >
                Date
              </div>
              <input
                className="form-control"
                type="date"
                value={metaDate}
                onFocus={blurNotesEditor}
                onChange={e => setMetaDate(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSaveMeta}
            >
              Save
            </button>
          </div>
          {metaError ? (
            <div className="text-danger mt-10">{metaError}</div>
          ) : null}
        </div>
      ) : null}

      <div className="mb-20">
        {editor ? (
          <div className="mb-10">
            <div
              style={{
                position: 'relative',
                display: 'inline-block'
              }}
            >
              <div
                className="btn-group btn-group-sm"
                role="group"
              >
                <button
                  type="button"
                  className={`btn btn-outline-secondary ${
                    editor.isActive('bold') ? 'active' : ''
                  }`}
                  aria-label="Bold"
                  title="Bold"
                  onClick={() =>
                    editor.chain().focus().toggleBold().run()
                  }
                >
                  <FontAwesomeIcon icon={faBold} />
                </button>
                <button
                  type="button"
                  className={`btn btn-outline-secondary ${
                    editor.isActive('italic') ? 'active' : ''
                  }`}
                  aria-label="Italic"
                  title="Italic"
                  onClick={() =>
                    editor.chain().focus().toggleItalic().run()
                  }
                >
                  <FontAwesomeIcon icon={faItalic} />
                </button>
                <button
                  type="button"
                  className={`btn btn-outline-secondary ${
                    editor.isActive('underline') ? 'active' : ''
                  }`}
                  aria-label="Underline"
                  title="Underline"
                  onClick={() =>
                    editor
                      .chain()
                      .focus()
                      .toggleUnderline()
                      .run()
                  }
                >
                  <FontAwesomeIcon icon={faUnderlineIcon} />
                </button>
                <button
                  type="button"
                  className={`btn btn-outline-secondary ${
                    editor.isActive('bulletList') ? 'active' : ''
                  }`}
                  aria-label="Bulleted list"
                  title="Bulleted list"
                  onClick={() =>
                    editor
                      .chain()
                      .focus()
                      .toggleBulletList()
                      .run()
                  }
                >
                  <FontAwesomeIcon icon={faListUl} />
                </button>
                <button
                  type="button"
                  className={`btn btn-outline-secondary ${
                    editor.isActive('orderedList')
                      ? 'active'
                      : ''
                  }`}
                  aria-label="Numbered list"
                  title="Numbered list"
                  onClick={() =>
                    editor
                      .chain()
                      .focus()
                      .toggleOrderedList()
                      .run()
                  }
                >
                  <FontAwesomeIcon icon={faListOl} />
                </button>
                <button
                  type="button"
                  className={`btn btn-outline-secondary ${
                    editor.isActive('link') ? 'active' : ''
                  }`}
                  aria-label="Link"
                  title="Link"
                  onClick={() => {
                    if (isLinkPopoverOpen) closeLinkPopover()
                    else openLinkPopover()
                  }}
                >
                  <FontAwesomeIcon icon={faLinkIcon} />
                </button>
              </div>

              {isLinkPopoverOpen ? (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    width: 320,
                    zIndex: 10,
                    background: 'white',
                    border: 'none',
                    borderRadius: 10,
                    padding: 10,
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)'
                  }}
                  onMouseDownCapture={e => e.stopPropagation()}
                  onKeyDownCapture={e => e.stopPropagation()}
                >
                  <div
                    className="text-muted"
                    style={{ fontSize: 12, marginBottom: 6 }}
                  >
                    Link URL
                  </div>
                  <input
                    ref={linkUrlInputRef}
                    className="form-control form-control-sm"
                    placeholder="https://example.com"
                    value={linkUrlDraft}
                    onChange={e =>
                      setLinkUrlDraft(e.target.value)
                    }
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        applyLink()
                      }
                      if (e.key === 'Escape') {
                        e.preventDefault()
                        closeLinkPopover()
                      }
                    }}
                  />
                  <div
                    className="d-flex justify-content-end mt-10"
                    style={{ gap: 8 }}
                  >
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={closeLinkPopover}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={applyLink}
                    >
                      OK
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        <div>
          {editor ? (
            <div className="collab-notes-shell">
              <EditorContent editor={editor} />
            </div>
          ) : null}
        </div>
      </div>

      <div className="mb-20">
        <div className="d-flex align-items-center justify-content-between mb-10">
          <div
            className="d-flex align-items-center"
            style={{ gap: 10 }}
          >
            <div className="font-weight-bold">Action Steps</div>
            <TouchableOpacity
              onClick={canEditSteps ? addStep : null}
              title="Add step"
              style={{
                opacity: canEditSteps ? 1 : 0.5,
                cursor: canEditSteps ? 'pointer' : 'not-allowed'
              }}
            >
              <IconPlusCircle />
            </TouchableOpacity>
          </div>
        </div>

        <div
          onMouseDownCapture={e => e.stopPropagation()}
          onKeyDownCapture={e => e.stopPropagation()}
          onBeforeInputCapture={e => e.stopPropagation()}
        >
          {stepsDraft.length === 0 ? (
            <div className="text-muted">No action steps.</div>
          ) : (
            stepsDraft.map(step => (
              <div
                key={`step-${step?.id}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '28px 1fr 36px',
                  gap: 10,
                  alignItems: 'center',
                  marginBottom: 10
                }}
              >
                <input
                  type="checkbox"
                  checked={!!step?.status}
                  onMouseDown={e => e.stopPropagation()}
                  onKeyDown={e => e.stopPropagation()}
                  onKeyDownCapture={e => e.stopPropagation()}
                  onFocus={() => {
                    blurNotesEditor()
                    markStepsEditing()
                  }}
                  onBlur={markStepsNotEditingSoon}
                  onChange={e =>
                    updateStep(step?.id, {
                      status: e.target.checked
                    })
                  }
                  disabled={!canEditSteps}
                />
                <input
                  className="form-control"
                  value={step?.description || ''}
                  onMouseDown={e => e.stopPropagation()}
                  onKeyDown={e => e.stopPropagation()}
                  onKeyDownCapture={e => e.stopPropagation()}
                  onFocus={() => {
                    blurNotesEditor()
                    markStepsEditing()
                  }}
                  onBlur={markStepsNotEditingSoon}
                  onChange={e =>
                    updateStep(step?.id, {
                      description: e.target.value
                    })
                  }
                  disabled={!canEditSteps}
                />
                <TouchableOpacity
                  onMouseDown={e => e.stopPropagation()}
                  onKeyDown={e => e.stopPropagation()}
                  onKeyDownCapture={e => e.stopPropagation()}
                  onClick={
                    canEditSteps
                      ? () => removeStep(step?.id)
                      : null
                  }
                  title="Delete step"
                  style={{
                    opacity: canEditSteps ? 1 : 0.5,
                    cursor: canEditSteps
                      ? 'pointer'
                      : 'not-allowed',
                    display: 'flex',
                    justifyContent: 'center'
                  }}
                >
                  <IconTrash
                    className="collab-trash-icon"
                    style={{ width: 18, height: 18 }}
                  />
                </TouchableOpacity>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

CoachingSessionCollabEditor.propTypes = {
  ydoc: PropTypes.object.isRequired,
  awareness: PropTypes.object,
  userIdentity: PropTypes.object,
  coachingSession: PropTypes.object,
  hasRemoteState: PropTypes.bool,
  isSaving: PropTypes.bool,
  embedded: PropTypes.bool,
  onSaveMeta: PropTypes.func,
  onDeleteSession: PropTypes.func,
  onSaveSteps: PropTypes.func
}

CoachingSessionCollabEditor.defaultProps = {
  awareness: null,
  userIdentity: null,
  coachingSession: null,
  hasRemoteState: false,
  isSaving: false,
  embedded: false,
  onSaveMeta: null,
  onDeleteSession: null,
  onSaveSteps: null
}

export default CoachingSessionCollabEditor
